import React, { useContext, useState } from 'react'
import {
  Box,
  CanvasProvider,
  colors,
  Flex,
  Menu,
  SecondaryButton,
  space,
  TertiaryButton,
  Text,
  Tooltip
} from '@workday/canvas-kit-react'
import {
  audioMaxIcon,
  audioMuteIcon,
  audioNoneIcon,
  caretDownIcon,
  checkIcon,
  editIcon,
  listViewIcon,
  locationIcon,
  mediaPlayIcon
} from '@workday/canvas-system-icons-web'
import SaveFeedDialog from './SaveFeedDialog'
import RespondRelatedActions from './RespondRelatedActions'
import AutoRefreshButton from './AutoRefreshButton'
import FeedConfiguration from '../models/FeedConfiguration'
import { MenuTheme } from '../../components/CanvasThemes'
import { UpdateToastContext } from '../../utils/UpdateToastContext'
import { RespondAppConfiguration } from '../models/RespondAppConfiguration'
import { loadSavedFeeds } from '../models/SavedFeeds'
import { AgencyModel } from '../../models/AgencyModel'
import { ScreenWidthCategory, UpdateConfirmationContext } from '../../utils'
import { SegmentedControl } from '@workday/canvas-kit-preview-react'
import AutoRefreshLine from './AutoRefreshLine'
import styled from '@emotion/styled'
import { ScreenWidthCategoryContext } from '../../utils/ScreenWidthCategoryContext'

export interface RespondMenuBarProps {
  configuration: FeedConfiguration
  appConfiguration: RespondAppConfiguration
  agencies: AgencyModel[]
  radioAgency: AgencyModel | null
  tabIndex: number,
  onConfigureFeed: () => void
  onUpdateConfiguration: (configuration: FeedConfiguration) => void
  onRefresh: (completion: () => void) => void
  onCreateNewFeed: (saveCurrent: boolean, name: string) => void
  onViewSavedFeeds: () => void
  onViewDisplaySettings: () => void
  onChangeRadio: (agency: AgencyModel | null) => void
  onTabChange: (tabIndex: number) => void
}

type IncidentView = 'list' | 'map'

const StyledText = styled(Text)<{isSmallScreenWidth: boolean}>`
  overflow: ${(props) => props.isSmallScreenWidth ? 'hidden' : 'unset' };
  text-wrap: ${(props) => props.isSmallScreenWidth ? 'nowrap' : 'unset' };
  text-overflow: ${(props) => props.isSmallScreenWidth ? 'ellipsis' : 'unset' };
  max-width: ${(props) => props.isSmallScreenWidth ? 'calc(40vw)' : 'unset' };
`

const RespondMenuBar = ({
  configuration,
  appConfiguration,
  agencies,
  radioAgency,
  tabIndex,
  onConfigureFeed,
  onUpdateConfiguration,
  onRefresh,
  onCreateNewFeed,
  onViewSavedFeeds,
  onViewDisplaySettings,
  onChangeRadio,
  onTabChange,
}: RespondMenuBarProps) => {
  const updateConfirmation = useContext(UpdateConfirmationContext)
  const toast = useContext(UpdateToastContext)
  const screenWidth = useContext(ScreenWidthCategoryContext)
  const isSmallScreenWidth = screenWidth < ScreenWidthCategory.Large
  const [view, setView] = useState<IncidentView>(tabIndex === 1 ? 'list' : 'map')

  let feedName: string
  if (configuration.name != '') {
    feedName = configuration.name
  } else {
    const agencyNames = agencies.map(agency => agency.shortName).sort()
    const agencyLabels =
      agencyNames.length <= 3 ? agencyNames : agencyNames.slice(0, 2).concat([`+ ${agencyNames.length - 2} more`])
    feedName = agencyLabels.join(', ')
  }

  const incidentTypesLabel =
    appConfiguration.context === 'PUBLIC'
      ? configuration.callTypes.length === 0
        ? 'All Incident Types'
        : `${configuration.callTypes.length} Incident Types`
      : ''
  const environmentLabel = configuration.environment === 'SANDBOX' ? 'Sandbox' : ''
  const psapLabel = configuration.showFullPSAP ? 'PSAP View' : ''
  const line2Label = [environmentLabel, psapLabel, incidentTypesLabel].filter(label => label.length > 0).join(' | ')
  const isSavedFeed = loadSavedFeeds().find(feed => configuration.id === feed.id)
  const showSave = appConfiguration.context === 'PUBLIC' && !isSavedFeed && !appConfiguration.isEmbedded
  const radioAgencies = agencies.filter(agency => agency.hasRadio())

  const selectableFeeds: FeedConfiguration[] = []
  if (appConfiguration.context === 'PUBLIC') {
    selectableFeeds.push(configuration)
  } else {
    appConfiguration.allowedEnvironments.forEach(environment => {
      selectableFeeds.push(configuration.copy().withEnvironment(environment).withShowFullPSAP(false))
      selectableFeeds.push(configuration.copy().withEnvironment(environment).withShowFullPSAP(true))
    })
  }

  return (
    <Box
      backgroundColor={colors.frenchVanilla100}
      boxShadow={`0px 0px 5px ${colors.blackPepper100}`}
      zIndex={3}
    >
      <Flex padding={space.s} flexDirection="row" flexWrap="wrap" gap={space.xs} alignItems="center">
        {selectableFeeds.length === 1 && (
          <Flex flexDirection="column">
            <StyledText typeLevel="body.small" fontWeight="medium" isSmallScreenWidth={isSmallScreenWidth}>
              {feedName}
            </StyledText>
            <Text typeLevel="subtext.large">{line2Label}</Text>
          </Flex>
        )}
        {selectableFeeds.length === 1 && !isSmallScreenWidth && (
          <Tooltip title="Edit Feed">
            <TertiaryButton icon={editIcon} onClick={event => onConfigureFeed()} />
          </Tooltip>
        )}
        {selectableFeeds.length > 1 && (
          <CanvasProvider theme={MenuTheme}>
            <Menu
              onSelect={data => {
                const idx = parseInt(data.id)
                onUpdateConfiguration(selectableFeeds[idx])
              }}
            >
              <Menu.Target as={SecondaryButton} icon={caretDownIcon}>
                {configuration.environment === 'PRODUCTION' ? 'Production' : 'Sandbox'} |{' '}
                {configuration.showFullPSAP ? 'PSAP' : feedName}
              </Menu.Target>
              <Menu.Popper>
                <Menu.Card>
                  <Menu.List>
                    {selectableFeeds.map((feed, idx) => (
                      <Menu.Item data-id={idx.toString()} key={idx.toString()}>
                        <Menu.Item.Text>
                          {feed.environment === 'PRODUCTION' ? 'Production' : 'Sandbox'} |{' '}
                          {feed.showFullPSAP ? 'PSAP' : 'Agency Only'}
                        </Menu.Item.Text>
                        {configuration.showFullPSAP === feed.showFullPSAP &&
                          configuration.environment === feed.environment && <Menu.Item.Icon icon={checkIcon} />}
                      </Menu.Item>
                    ))}
                  </Menu.List>
                </Menu.Card>
              </Menu.Popper>
            </Menu>
          </CanvasProvider>
        )}
        {showSave && !isSmallScreenWidth && (
          <SaveFeedDialog
            defaultName={feedName}
            onSave={name => {
              const c = configuration.copy()
              c.name = name
              c.type = 'SAVED'
              onUpdateConfiguration(c)
            }}
          />
        )}
        <Flex.Item flex={1} />
        {isSmallScreenWidth && <SegmentedControl
            initialValue={view}
            onSelect={(value: any) => {
              setView(value.id)
              onTabChange(value.id === 'list' ? 1 : 2)
            }}
          >
            <SegmentedControl.List aria-label="View Type">
              <SegmentedControl.Item data-id="list" icon={listViewIcon} tooltipProps={{ title: 'List View' }} />
              <SegmentedControl.Item data-id="map" icon={locationIcon} tooltipProps={{ title: 'Map View' }} />
            </SegmentedControl.List>
          </SegmentedControl>
        }
        {radioAgencies.length === 1 
        ? (
          <Tooltip title={isSmallScreenWidth 
            ? (radioAgency ? radioAgency.shortName : 'Live Radio') 
            : (radioAgency ? 'Stop': 'Play') }
          >
            <SecondaryButton 
              icon={radioAgency ? audioMaxIcon : audioNoneIcon}
              onClick={event => {
                radioAgency ? onChangeRadio(null) : onChangeRadio(radioAgencies[0])
              }}
            >
              {isSmallScreenWidth ? '' : radioAgency ? radioAgency.shortName : 'Live Radio'}
            </SecondaryButton>
          </Tooltip>
        ) 
        : radioAgencies.length > 0 ? (
          <CanvasProvider theme={MenuTheme}>
            <Menu
              onSelect={data => {
                if (data.id === 'off') {
                  onChangeRadio(null)
                } else {
                  onChangeRadio(radioAgencies.find(agency => agency.agencyID === data.id) ?? null)
                }
              }}
            >
              <Menu.Target as={SecondaryButton} icon={radioAgency ? audioMaxIcon : audioNoneIcon}>
                {isSmallScreenWidth ? '' : radioAgency ? radioAgency.shortName : 'Live Radio'}
              </Menu.Target>
              <Menu.Popper>
                <Menu.Card>
                  <Menu.List>
                    {radioAgencies.map(agency => (
                      <Menu.Item data-id={agency.agencyID} key={agency.agencyID}>
                        <Menu.Item.Icon
                          icon={agency.agencyID === radioAgency?.agencyID ? audioMaxIcon : mediaPlayIcon}
                        />
                        <Menu.Item.Text>{agency.shortName}</Menu.Item.Text>
                      </Menu.Item>
                    ))}
                    {radioAgency !== null && <Menu.Divider />}
                    {radioAgency !== null && (
                      <Menu.Item data-id="off">
                        <Menu.Item.Icon icon={audioNoneIcon}></Menu.Item.Icon>
                        <Menu.Item.Text>Stop</Menu.Item.Text>
                      </Menu.Item>
                    )}
                  </Menu.List>
                </Menu.Card>
              </Menu.Popper>
            </Menu>
          </CanvasProvider>
        ) : (
          <SecondaryButton
            icon={audioMuteIcon}
            onClick={event => {
              toast.setMessages([], ['Live Radio unavailable for the agencies in this feed.'])
            }}
          >
            {isSmallScreenWidth ? '' : "No Radio"}
          </SecondaryButton>
        )}
        {!isSmallScreenWidth && 
          <AutoRefreshButton onRefresh={onRefresh} refreshIntervalSeconds={30} />
        }
        <RespondRelatedActions
          appConfiguration={appConfiguration}
          showSave={showSave}
          defaultName={feedName}
          onConfigureFeed={onConfigureFeed}
          onSaveFeed={name => {
            const c = configuration.copy()
            c.name = name
            c.type = 'SAVED'
            onUpdateConfiguration(c)
          }}
          onCreateFeed={() => {
            if (!isSavedFeed) {
              updateConfirmation.setConfirmation({
                title: 'Saved Current Feed?',
                message: 'Do you want to save your current feed before continuing?',
                confirmLabel: 'Yes',
                onConfirm: () => onCreateNewFeed(true, feedName),
                otherLabel: 'No',
                onOtherButton: () => onCreateNewFeed(false, ''),
                onCancel: () => {}
              })
            } else {
              onCreateNewFeed(false, '')
            }
          }}
          onViewSavedFeeds={onViewSavedFeeds}
          onViewDisplaySettings={onViewDisplaySettings}
        />
      </Flex>
      {isSmallScreenWidth && 
        <Flex>
          <Flex.Item flex={1}>
            <AutoRefreshLine onRefresh={onRefresh} refreshIntervalSeconds={30} isSmallScreenWidth={isSmallScreenWidth} />
          </Flex.Item>
        </Flex>
      }
    </Box>
  )
}

export default RespondMenuBar
